import React, { useState, useEffect, memo } from "react";
import { StockCheckExtract, StockCheckExtractLine } from "../../interfaces/Sce";
import { ReactComponent as SubtractIcon } from "../../assets/icons/subtract-circle-32-regular.svg?react";
import { ReactComponent as AddIcon } from "../../assets/icons/add-circle-32-regular.svg?react";
import { ReactComponent as SubmitIcon } from "../../assets/icons/box-checkmark-24-regular.svg?react";
import { ReactComponent as EditIcon } from "../../assets/icons/edit-whitepencil.svg?react";
import { ReactComponent as UnsavedIcon } from "../../assets/icons/unsaved-icon.svg?react";
import { ReactComponent as NegativeStockIcon } from "../../assets/icons/negative-stock-warning.svg?react";
import { ReactComponent as BatchIcon } from "../../assets/icons/batch-28-regular.svg?react";
import { toast, Slide } from "react-toastify";

export interface StockExtractLineCardProps {
  sce: StockCheckExtract;
  sceLine: StockCheckExtractLine;
  updateLine(line: StockCheckExtractLine): void;
  addToFinishLines(line: StockCheckExtractLine): void;
  removeFromFinishLines(line: StockCheckExtractLine): void;
}

const StockExtractLineCard = memo((props: StockExtractLineCardProps) => {
  const [amount, setAmount] = useState(props.sceLine.qtySubmitted ?? 0);
  const [lineDisplayQty, setLineDisplayQty] = useState(
    (props.sceLine.qtySubmitted ?? 0).toString()
  );
  const regexp = new RegExp(/^(\d{1,6}\.\d{1,3}|\d{1,6}\.|\d{1,6})$/);

  const closeAllCards = (id?: string) => {
    const listOfCards = [
      ...document.getElementsByClassName("stockextract-line-card"),
    ];
    listOfCards.forEach((card) => {
      if (id) {
        if (card.id != id) {
          // close the card
          card.removeAttribute("open");
        }
      } else {
        card.removeAttribute("open");
      }
    });
  };

  const animateDetailsContent = () => {
    const detailsContent = document
      .getElementById("stockextract-line-card-" + props.sceLine.partNo)
      ?.querySelector(".extract-line-information");

    if (detailsContent) {
      detailsContent.classList.add("animate");
      setTimeout(() => {
        detailsContent.classList.remove("animate");
      }, 300);
    }
  };

  useEffect(() => {
    if (props.sceLine.expandTrigger) {
      document
        .getElementById("stockextract-line-card-" + props.sceLine.partNo)
        ?.querySelector("summary")
        ?.click();
      closeAllCards("stockextract-line-card-" + props.sceLine.partNo);
      props.sceLine.expandTrigger = false;
    }
  }, [props.sceLine.expandTrigger]);

  const handleAddPress = () => {
    if (Number((Number(lineDisplayQty) + 1).toFixed(3)) < 1000000) {
      if (Number((Number(lineDisplayQty) + 1).toFixed(3)) % 1 != 0) {
        setLineDisplayQty((prevDisplay) =>
          Math.ceil(Number(prevDisplay)).toString()
        );
      } else {
        setLineDisplayQty((prevDisplay) =>
          Number((Number(prevDisplay) + 1).toFixed(3)).toString()
        );
      }
    } else {
      // toast message
      toast.error("Maximum whole number entered", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
        transition: Slide,
      });
    }
  };

  const handleSubtractPress = () => {
    if (Number((Number(lineDisplayQty) - 1).toFixed(3)) >= 0) {
      if (Number((Number(lineDisplayQty) - 1).toFixed(3)) % 1 != 0) {
        setLineDisplayQty((prevDisplay) =>
          Math.floor(Number(prevDisplay)).toString()
        );
      } else {
        setLineDisplayQty((prevDisplay) =>
          Number((Number(prevDisplay) - 1).toFixed(3)).toString()
        );
      }
    } else {
      // toast message
      toast.error("Value Cannot Be Negative", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
        transition: Slide,
      });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleOnChange = (event: any) => {
    const textValue: string = event.target.value;

    if (regexp.test(textValue) || textValue == "") {
      setLineDisplayQty(textValue);
    } else {
      if (isNaN(Number(textValue))) {
        toast.error('Non-Numeric Value Entered: "' + textValue + '"', {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
          transition: Slide,
        });
        return;
      }

      if (
        (textValue.length > 6 && !textValue.includes(".")) ||
        textValue.length > 10
      ) {
        toast.error("Maximum number of digits entered", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
          transition: Slide,
        });
        return;
      }

      toast.error('Invalid Value Entered: "' + textValue + '"', {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
        transition: Slide,
      });
    }
  };

  const handleOnSubmit = () => {
    if (lineDisplayQty == "") {
      setAmount(0);
    } else {
      setAmount(Number(Number(lineDisplayQty).toFixed(3)));
    }
    props.sceLine.qtySubmitted = Number(lineDisplayQty);
    if (!props.sceLine.wasAlreadyCounted) {
      // Change status unless Gold provided one
      props.sceLine.status =
        props.sceLine.stockQuantity === Number(amount)
          ? "Counted"
          : "Difference";
    }

    props.sceLine.indicatorStatus =
      props.sceLine.stockQuantity === Number(Number(lineDisplayQty).toFixed(3))
        ? "Correct"
        : "Incorrect";
    props.sceLine.hasUnpublishedChanges = true;
    props.sceLine.hasUnsavedChanges = true;
    //props.updateLine(props.sceLine);
    props.addToFinishLines(props.sceLine);
  };

  const handleEditPress = () => {
    if (!props.sceLine.wasAlreadyCounted) {
      // Change status unless Gold provided one
      props.sceLine.status = "No Input";
    }
    props.sceLine.qtySubmitted = null;
    props.sceLine.indicatorStatus =
      props.sceLine.stockQuantity === Number(amount) ? "Correct" : "Incorrect";
    props.sceLine.hasUnpublishedChanges = false;
    props.sceLine.hasUnsavedChanges = false;
    props.sceLine.indicatorStatus = "None";
    //props.updateLine(props.sceLine);
    props.removeFromFinishLines(props.sceLine);
    setLineDisplayQty(amount.toString());
  };

  return (
    <details
      id={"stockextract-line-card-" + props.sceLine.partNo}
      className={
        "stockextract-line-card" +
        (props.sceLine.wasAlreadyCounted ? " alreadyCounted" : "") +
        (props.sceLine.partType == "Batch" || props.sceLine.isNegativeStock
          ? " batchPart"
          : "")
      }
    >
      <summary
        className="extract-line-summary"
        onClick={() => {
          animateDetailsContent();
          closeAllCards("stockextract-line-card-" + props.sceLine.partNo);
        }}
      >
        <div className="extract-line-title">
          {props.sceLine.partType == "Batch" ? (
            <BatchIcon
              className="serial-icon-svg"
              id={props.sceLine.partNo + "-serial-icon"}
            ></BatchIcon>
          ) : (
            ""
          )}
          <span>{props.sceLine.partNo}</span>
          {props.sceLine.isNegativeStock ? (
            <>
              <NegativeStockIcon className="negativeStock-icon" />
            </>
          ) : props.sceLine.hasUnpublishedChanges ? (
            <>
              <UnsavedIcon className="saveState-icon" />
            </>
          ) : (
            ""
          )}

          {props.sceLine.stockQuantity != null &&
          !props.sceLine.isNegativeStock ? (
            <>
              <div
                id="stockextract-line-indicator"
                className={
                  "extract-line-count-indicator " +
                  props.sceLine.indicatorStatus
                }
              ></div>
            </>
          ) : (
            ""
          )}
        </div>
        <span className="extract-line-description">
          {props.sceLine.partDescription}
        </span>
        <span className="extract-line-bin-location">
          Location:&nbsp;&nbsp;
          <span className="bold">{props.sceLine.binLocation}</span>
        </span>
      </summary>

      <div className="extract-line-information">
        {props.sceLine.partType == "Batch" ? (
          <span className="extract-line-information-batch">
            <p>This is a Batch Part, please process it on Gold</p>
          </span>
        ) : props.sceLine.isNegativeStock == false ? (
          <>
            <div className="extract-line-information-count">
              <p>
                Counted:{" "}
                {props.sceLine.qtySubmitted == null ? (
                  <>--</>
                ) : (
                  props.sceLine.qtySubmitted
                )}
                {props.sceLine.stockQuantity != null ? (
                  <>/ {props.sceLine.stockQuantity}</>
                ) : (
                  ""
                )}
              </p>
            </div>
            {props.sceLine.shortShip != null ? (
              <>
                <div className="extract-line-information-ship-wip">
                  <p>
                    {props.sceLine.shortShip != null
                      ? `Short Ship: ${props.sceLine.shortShip}`
                      : ""}
                  </p>
                  <p>
                    {props.sceLine.wipQuantity != null
                      ? `Wip Qty: ${props.sceLine.wipQuantity}`
                      : ""}
                  </p>
                </div>
              </>
            ) : (
              ""
            )}
            <div className="extract-line-information-buttons">
              {props.sceLine.qtySubmitted == null ? (
                <>
                  <button
                    id={props.sceLine.partNo + "-button-subtract"}
                    className="extract-line-button extract-line-subtract"
                    onClick={handleSubtractPress}
                  >
                    <SubtractIcon />
                  </button>
                  <div className="extract-line-input">
                    <input
                      id={props.sceLine.partNo + "-input-field"}
                      type="text"
                      inputMode="decimal"
                      pattern={regexp.toString()}
                      value={lineDisplayQty}
                      step="0.1"
                      onChange={(event) => {
                        handleOnChange(event);
                      }}
                    />
                  </div>
                  <button
                    id={props.sceLine.partNo + "-button-add"}
                    className="extract-line-button extract-line-add"
                    onClick={handleAddPress}
                  >
                    <AddIcon />
                  </button>
                  <button
                    id={props.sceLine.partNo + "-button-submit"}
                    className="extract-line-button extract-line-complete"
                    onClick={handleOnSubmit}
                  >
                    <SubmitIcon />
                  </button>
                </>
              ) : (
                <>
                  <span className="extract-line-button-spacer"></span>
                  <span className="extract-line-button-spacer"></span>
                  <span className="extract-line-button-spacer"></span>
                  <button
                    id={props.sceLine.partNo + "-button-edit"}
                    className="extract-line-button"
                    onClick={handleEditPress}
                  >
                    <EditIcon />
                  </button>
                </>
              )}
            </div>
          </>
        ) : (
          <span className="extract-line-information-batch">
            <p>This Part has Negative Stock, please process it on Gold</p>
          </span>
        )}
      </div>
    </details>
  );
});

StockExtractLineCard.displayName = "StockExtractLineCard";
export default StockExtractLineCard;
